import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import FavoritesTab from './Dashboard/FavoritesTab'
import BookingsTab from './Dashboard/BookingsTab'
import NotificationsTab from './Dashboard/NotificationsTab'

const Sidebar = ({
  isOpen,
  onClose,
  type = null, // 'favorites', 'bookings', or 'notifications'
  // Favorites props
  favoriteTab,
  setFavoriteTab,
  favoriteServices,
  favoriteSpecialists,
  favoriteServicesWithAssociations,
  favoriteProfessionalsWithAssociations,
  favoriteAppointments,
  categories,
  currency,
  toggleFavorite,
  onBookNow,
  setSelectedFavoriteSpecialist,
  setShowFavoriteModal,
  setBookingData,
  setShowServiceBooking,
  setServiceBookingStep,
  onRemoveFavoriteAppointment,
  // Bookings props
  bookingFilter,
  setBookingFilter,
  upcomingBookings,
  completedBookings,
  cancelledBookings,
  missedBookings,
  allBookings,
  appointments,
  refreshingAppointments,
  refreshAppointments,
  getFilteredBookings,
  calculateBookingTotal,
  calculateBookingDuration,
  formatDateForDisplay,
  formatTimeForDisplay,
  getPaymentStatus,
  isAppointmentExpired,
  isAppointmentAtLeastOneHourAway,
  canCancelAppointment,
  canRescheduleAppointment,
  getRescheduleBlockReason,
  getBookingProfessionalDisplay,
  getBookingProfessionalsForReview,
  getDealProfessionalsForReview,
  getDealReviewKey,
  parseServiceDuration,
  calculateServiceTimeRange,
  handleCancelAppointment,
  handleRemoveBooking,
  handleClearAllCancelled,
  handleReschedule,
  handleBookAgain,
  toggleBookingCollapse,
  collapsedBookings,
  toggleReview,
  handleReviewRating,
  handleReviewComment,
  handleSubmitReview,
  showReviewForBooking,
  reviewData,
  activeReviewTab,
  setActiveReviewProfessional,
  submittingReviewBookingId,
  cancellingBookingId,
  // Notifications props
  notificationFeed,
  markAllNotificationsRead,
  unreadNotificationCount,
  onDeclineReassignment,
  onKeepAppointment,
  onNotificationClick,
  processingNotificationId,
}) => {
  const navigate = useNavigate()
  const hasRefreshedRef = useRef(false)
  const sidebarStateRef = useRef({ isOpen, type })

  // Keep ref updated with current state
  useEffect(() => {
    sidebarStateRef.current = { isOpen, type }
  }, [isOpen, type])

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Keep latest refresh fn in a ref so open/close effects don't re-fire every render
  // (refreshAppointments is recreated each parent render → would infinite-loop setState)
  const refreshAppointmentsRef = useRef(refreshAppointments)
  useEffect(() => {
    refreshAppointmentsRef.current = refreshAppointments
  }, [refreshAppointments])

  // Auto-refresh appointments when bookings sidebar opens (once per open)
  useEffect(() => {
    if (isOpen && type === 'bookings' && refreshAppointmentsRef.current) {
      refreshAppointmentsRef.current()
    }
  }, [isOpen, type])

  // Refresh appointments when tab becomes visible again (user switches back to tab)
  // Only active when sidebar is open - uses ref to check current state
  useEffect(() => {
    if (!isOpen || type !== 'bookings') {
      return
    }

    const handleVisibilityChange = () => {
      const currentState = sidebarStateRef.current
      if (
        document.visibilityState === 'visible' &&
        currentState.isOpen &&
        currentState.type === 'bookings' &&
        refreshAppointmentsRef.current
      ) {
        refreshAppointmentsRef.current()
      }
    }

    const handleFocus = () => {
      const currentState = sidebarStateRef.current
      if (
        currentState.isOpen &&
        currentState.type === 'bookings' &&
        refreshAppointmentsRef.current
      ) {
        refreshAppointmentsRef.current()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [isOpen, type])

  // Only render content if we have a type (favorites or bookings)
  if (!type) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />
      
      {/* Sidebar */}
      <div className={`fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-[9999] transform transition-transform duration-300 ease-in-out overflow-hidden flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200/60 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <i className={`fas ${
                type === 'favorites' ? 'fa-heart' : 
                type === 'notifications' ? 'fa-bell' : 
                'fa-calendar-check'
              } text-white text-lg`}></i>
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                {type === 'favorites' ? 'My Favorites' : 
                 type === 'notifications' ? 'Notifications' : 
                 'My Bookings'}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {type === 'favorites' ? 'Your saved items' : 
                 type === 'notifications' ? 'Latest updates about your appointments' : 
                 'Manage your appointments'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 text-gray-500 hover:text-gray-900 group"
            aria-label="Close sidebar"
          >
            <i className="fas fa-times text-lg group-hover:rotate-90 transition-transform duration-200"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50/30 scrollbar-thin scrollbar-thumb-purple-300 scrollbar-track-gray-100 hover:scrollbar-thumb-purple-400">
          {type === 'notifications' ? (
            <NotificationsTab
              notificationFeed={notificationFeed || []}
              markAllNotificationsRead={markAllNotificationsRead}
              unreadCount={unreadNotificationCount || 0}
              onDeclineReassignment={onDeclineReassignment}
              onKeepAppointment={onKeepAppointment}
              onNotificationClick={onNotificationClick}
              processingNotificationId={processingNotificationId}
            />
          ) : type === 'favorites' ? (
            <FavoritesTab
              favoriteTab={favoriteTab}
              setFavoriteTab={setFavoriteTab}
              favoriteServices={favoriteServices}
              favoriteSpecialists={favoriteSpecialists}
              favoriteServicesWithAssociations={favoriteServicesWithAssociations}
              favoriteProfessionalsWithAssociations={favoriteProfessionalsWithAssociations}
              favoriteAppointments={favoriteAppointments}
              categories={categories}
              currency={currency}
              toggleFavorite={toggleFavorite}
              onBookNow={onBookNow}
              setSelectedFavoriteSpecialist={setSelectedFavoriteSpecialist}
              setShowFavoriteModal={setShowFavoriteModal}
              navigate={navigate}
              onCloseSidebar={onClose}
              setBookingData={setBookingData}
              setShowServiceBooking={setShowServiceBooking}
              setServiceBookingStep={setServiceBookingStep}
              onRemoveFavoriteAppointment={onRemoveFavoriteAppointment}
            />
          ) : (
            <BookingsTab
              bookingFilter={bookingFilter}
              setBookingFilter={setBookingFilter}
              upcomingBookings={upcomingBookings || []}
              completedBookings={completedBookings || []}
              cancelledBookings={cancelledBookings || []}
              missedBookings={missedBookings || []}
              allBookings={allBookings || []}
              appointments={appointments || []}
              refreshingAppointments={refreshingAppointments || false}
              refreshAppointments={refreshAppointments || (() => {})}
              getFilteredBookings={getFilteredBookings || (() => [])}
              calculateBookingTotal={calculateBookingTotal}
              calculateBookingDuration={calculateBookingDuration}
              formatDateForDisplay={formatDateForDisplay}
              formatTimeForDisplay={formatTimeForDisplay}
              getPaymentStatus={getPaymentStatus}
              isAppointmentExpired={isAppointmentExpired}
              isAppointmentAtLeastOneHourAway={isAppointmentAtLeastOneHourAway}
              canCancelAppointment={canCancelAppointment}
              canRescheduleAppointment={canRescheduleAppointment}
              getRescheduleBlockReason={getRescheduleBlockReason}
              getBookingProfessionalDisplay={getBookingProfessionalDisplay}
              getBookingProfessionalsForReview={getBookingProfessionalsForReview}
              getDealProfessionalsForReview={getDealProfessionalsForReview}
              getDealReviewKey={getDealReviewKey}
              parseServiceDuration={parseServiceDuration}
              calculateServiceTimeRange={calculateServiceTimeRange}
              handleCancelAppointment={handleCancelAppointment || (() => {})}
              handleRemoveBooking={handleRemoveBooking || (() => {})}
              handleClearAllCancelled={handleClearAllCancelled || (() => {})}
              handleReschedule={handleReschedule || (() => {})}
              handleBookAgain={handleBookAgain || (() => {})}
              toggleBookingCollapse={toggleBookingCollapse || (() => {})}
              collapsedBookings={collapsedBookings || {}}
              toggleReview={toggleReview || (() => {})}
              handleReviewRating={handleReviewRating || (() => {})}
              handleReviewComment={handleReviewComment || (() => {})}
              handleSubmitReview={handleSubmitReview || (() => {})}
              showReviewForBooking={showReviewForBooking || {}}
              reviewData={reviewData || {}}
              activeReviewTab={activeReviewTab || {}}
              setActiveReviewProfessional={setActiveReviewProfessional || (() => {})}
              submittingReviewBookingId={submittingReviewBookingId || null}
              cancellingBookingId={cancellingBookingId || null}
              currency={currency}
              onBookNow={onBookNow}
              onCloseSidebar={onClose}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default Sidebar

